import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  Mail,
  Phone,
  Globe,
  MapPin,
  Users,
  UserCheck,
  UserX,
  Filter,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Card } from '../components/Card';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { StatCard } from '../components/StatCard';
import { prospectsApi, type Prospect } from '../lib/api';

const STATUS_OPTIONS = [
  { value: 'not_contacted', label: 'Not Contacted', color: 'default' },
  { value: 'emailed', label: 'Emailed', color: 'info' },
  { value: 'called', label: 'Called', color: 'warning' },
  { value: 'meeting_scheduled', label: 'Meeting Scheduled', color: 'accent' },
  { value: 'converted', label: 'Converted', color: 'success' },
  { value: 'not_interested', label: 'Not Interested', color: 'error' },
] as const;

const PAGE_SIZE = 50;

export function Prospects() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [emailOnlyFilter, setEmailOnlyFilter] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);

  // Fetch prospects
  const { data, isLoading } = useQuery({
    queryKey: ['prospects', searchQuery, categoryFilter, statusFilter, emailOnlyFilter, currentPage],
    queryFn: () =>
      prospectsApi.list({
        search: searchQuery || undefined,
        search_term: categoryFilter || undefined,
        contact_status: statusFilter || undefined,
        has_email: emailOnlyFilter || undefined,
        limit: PAGE_SIZE,
        offset: currentPage * PAGE_SIZE,
      }),
  });

  // Fetch categories for filter
  const { data: categoriesData } = useQuery({
    queryKey: ['prospect-categories'],
    queryFn: () => prospectsApi.categories(),
  });

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['prospect-stats'],
    queryFn: () => prospectsApi.stats(),
  });

  // Update status mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, contact_status }: { id: number; contact_status: string }) =>
      prospectsApi.update(id, { contact_status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prospects'] });
      queryClient.invalidateQueries({ queryKey: ['prospect-stats'] });
    },
  });

  const prospects = data?.prospects || [];
  const totalCount = data?.count || 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const categories = categoriesData?.categories || [];
  const stats = statsData || { total: 0, with_email: 0, contacted: 0, converted: 0 };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="animate-fade-in-up">
        <h1 className="text-2xl font-semibold text-[var(--asi-text)] tracking-tight">
          Prospects
        </h1>
        <p className="text-[var(--asi-text-secondary)] mt-1">
          Construction directory - {totalCount.toLocaleString()} companies
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="animate-fade-in-up delay-75">
          <StatCard
            title="Total Prospects"
            value={stats.total.toLocaleString()}
            icon={<Users className="w-5 h-5" />}
          />
        </div>
        <div className="animate-fade-in-up delay-150">
          <StatCard
            title="With Email"
            value={stats.with_email.toLocaleString()}
            icon={<Mail className="w-5 h-5" />}
            variant="accent"
          />
        </div>
        <div className="animate-fade-in-up delay-225">
          <StatCard
            title="Contacted"
            value={stats.contacted.toLocaleString()}
            icon={<UserCheck className="w-5 h-5" />}
          />
        </div>
        <div className="animate-fade-in-up delay-300">
          <StatCard
            title="Converted"
            value={stats.converted.toLocaleString()}
            icon={<UserX className="w-5 h-5" />}
          />
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 animate-fade-in-up">
        <div className="relative flex-1 min-w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--asi-text-muted)]" />
          <input
            type="text"
            placeholder="Search by name, email, phone..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(0);
            }}
            className="w-full pl-10 pr-4 py-2.5 bg-[var(--asi-bg)] border border-[var(--asi-bg-tertiary)] rounded-xl text-[var(--asi-text)] placeholder:text-[var(--asi-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--asi-accent)] focus:border-transparent transition-all"
          />
        </div>

        <select
          value={categoryFilter}
          onChange={(e) => {
            setCategoryFilter(e.target.value);
            setCurrentPage(0);
          }}
          className="px-4 py-2.5 bg-[var(--asi-bg)] border border-[var(--asi-bg-tertiary)] rounded-xl text-[var(--asi-text)] focus:outline-none focus:ring-2 focus:ring-[var(--asi-accent)] cursor-pointer transition-all"
        >
          <option value="">All Trades</option>
          {categories.map((cat) => (
            <option key={cat.search_term} value={cat.search_term}>
              {cat.search_term} ({cat.count})
            </option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(0);
          }}
          className="px-4 py-2.5 bg-[var(--asi-bg)] border border-[var(--asi-bg-tertiary)] rounded-xl text-[var(--asi-text)] focus:outline-none focus:ring-2 focus:ring-[var(--asi-accent)] cursor-pointer transition-all"
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map((status) => (
            <option key={status.value} value={status.value}>
              {status.label}
            </option>
          ))}
        </select>

        <button
          onClick={() => {
            setEmailOnlyFilter(!emailOnlyFilter);
            setCurrentPage(0);
          }}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all ${
            emailOnlyFilter
              ? 'bg-[var(--asi-accent)] border-[var(--asi-accent)] text-[var(--asi-text)]'
              : 'bg-[var(--asi-bg)] border-[var(--asi-bg-tertiary)] text-[var(--asi-text-secondary)] hover:border-[var(--asi-accent)]'
          }`}
        >
          <Filter className="w-4 h-4" />
          Has Email
        </button>
      </div>

      {/* Prospects Table */}
      <Card className="animate-fade-in-up overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--asi-bg-tertiary)]">
                <th className="text-left py-4 px-5 text-xs font-medium text-[var(--asi-text-secondary)] uppercase tracking-wider">
                  Company
                </th>
                <th className="text-left py-4 px-5 text-xs font-medium text-[var(--asi-text-secondary)] uppercase tracking-wider">
                  Contact
                </th>
                <th className="text-left py-4 px-5 text-xs font-medium text-[var(--asi-text-secondary)] uppercase tracking-wider">
                  Trade
                </th>
                <th className="text-left py-4 px-5 text-xs font-medium text-[var(--asi-text-secondary)] uppercase tracking-wider">
                  Location
                </th>
                <th className="text-left py-4 px-5 text-xs font-medium text-[var(--asi-text-secondary)] uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--asi-bg-tertiary)]">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center">
                    <div className="w-6 h-6 mx-auto border-2 border-[var(--asi-accent)] border-t-transparent rounded-full animate-spin" />
                  </td>
                </tr>
              ) : prospects.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-[var(--asi-bg-tertiary)] flex items-center justify-center">
                      <Users className="w-6 h-6 text-[var(--asi-text-muted)]" />
                    </div>
                    <p className="text-[var(--asi-text-secondary)]">No prospects found</p>
                    <p className="text-sm text-[var(--asi-text-muted)] mt-1">
                      Try adjusting your filters
                    </p>
                  </td>
                </tr>
              ) : (
                prospects.map((prospect, idx) => (
                  <ProspectRow
                    key={prospect.id}
                    prospect={prospect}
                    index={idx}
                    onStatusChange={(status) =>
                      updateMutation.mutate({ id: prospect.id, contact_status: status })
                    }
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-4 border-t border-[var(--asi-bg-tertiary)]">
            <p className="text-sm text-[var(--asi-text-secondary)]">
              Showing {currentPage * PAGE_SIZE + 1} -{' '}
              {Math.min((currentPage + 1) * PAGE_SIZE, totalCount)} of {totalCount.toLocaleString()}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                disabled={currentPage === 0}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-[var(--asi-text)]">
                Page {currentPage + 1} of {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={currentPage >= totalPages - 1}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

function ProspectRow({
  prospect,
  index,
  onStatusChange,
}: {
  prospect: Prospect;
  index: number;
  onStatusChange: (status: string) => void;
}) {
  return (
    <tr
      className="hover:bg-[var(--asi-bg-secondary)] transition-colors animate-slide-in"
      style={{ animationDelay: `${index * 20}ms` }}
    >
      <td className="py-4 px-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[var(--asi-accent-light)] rounded-xl flex items-center justify-center flex-shrink-0">
            <span className="text-[var(--asi-text)] font-semibold text-sm">
              {prospect.name?.[0]?.toUpperCase() || '?'}
            </span>
          </div>
          <div className="min-w-0">
            <p className="font-medium text-[var(--asi-text)] truncate">
              {prospect.name}
            </p>
            {prospect.website && (
              <a
                href={prospect.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-[var(--asi-accent)] hover:underline"
              >
                <Globe className="w-3 h-3" />
                Website
              </a>
            )}
          </div>
        </div>
      </td>
      <td className="py-4 px-5">
        <div className="space-y-1">
          {prospect.email && (
            <a
              href={`mailto:${prospect.email}`}
              className="flex items-center gap-2 text-sm text-[var(--asi-text)] hover:text-[var(--asi-accent)]"
            >
              <Mail className="w-4 h-4 text-[var(--asi-text-muted)]" />
              {prospect.email}
            </a>
          )}
          {prospect.phone && (
            <a
              href={`tel:${prospect.phone}`}
              className="flex items-center gap-2 text-sm text-[var(--asi-text)] hover:text-[var(--asi-accent)]"
            >
              <Phone className="w-4 h-4 text-[var(--asi-text-muted)]" />
              {prospect.phone}
            </a>
          )}
          {!prospect.email && !prospect.phone && (
            <span className="text-sm text-[var(--asi-text-muted)]">No contact info</span>
          )}
        </div>
      </td>
      <td className="py-4 px-5">
        <Badge variant="default" size="sm">
          {prospect.search_term || 'Unknown'}
        </Badge>
      </td>
      <td className="py-4 px-5">
        {prospect.address ? (
          <div className="flex items-center gap-2 text-sm text-[var(--asi-text-secondary)]">
            <MapPin className="w-4 h-4 text-[var(--asi-text-muted)]" />
            <span className="truncate max-w-48">{prospect.address}</span>
          </div>
        ) : (
          <span className="text-sm text-[var(--asi-text-muted)]">-</span>
        )}
      </td>
      <td className="py-4 px-5">
        <select
          value={prospect.contact_status}
          onChange={(e) => onStatusChange(e.target.value)}
          className={`px-2.5 py-1 rounded-full text-xs font-medium border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[var(--asi-accent)] ${
            prospect.contact_status === 'not_contacted'
              ? 'bg-[var(--asi-bg-tertiary)] text-[var(--asi-text-secondary)]'
              : prospect.contact_status === 'emailed'
              ? 'bg-[var(--asi-info-light)] text-[var(--asi-info)]'
              : prospect.contact_status === 'called'
              ? 'bg-[var(--asi-warning-light)] text-[var(--asi-warning)]'
              : prospect.contact_status === 'meeting_scheduled'
              ? 'bg-[var(--asi-accent-light)] text-[var(--asi-text)]'
              : prospect.contact_status === 'converted'
              ? 'bg-[var(--asi-success-light)] text-[var(--asi-success)]'
              : 'bg-[var(--asi-error-light)] text-[var(--asi-error)]'
          }`}
        >
          {STATUS_OPTIONS.map((status) => (
            <option key={status.value} value={status.value}>
              {status.label}
            </option>
          ))}
        </select>
      </td>
    </tr>
  );
}
